/*
 * VerideCloudinaryUploader — shared, dependency-free helper that uploads a
 * single image straight from the browser to Cloudinary using an UNSIGNED
 * upload preset, then hands back the resulting `public_id`.
 *
 * No secrets live here: the cloud name and the unsigned preset name are both
 * public values. The preset itself (configured in the Cloudinary dashboard)
 * is where security lives — authenticated/private delivery, allowed formats,
 * max size, folder, moderation. See the theme's gift-form docs.
 *
 * Usage:
 *   var up = VerideCloudinaryUploader.create({
 *     input: fileInputEl,
 *     cloudName: 'xxx',
 *     uploadPreset: 'yyy',
 *     folder: 'gift-box-photos',
 *     maxMb: 10,
 *     onStart:    function (previewUrl, file) {},
 *     onProgress: function (percent) {},
 *     onSuccess:  function (publicId, response) {},
 *     onError:    function (code) {}   // 'config' | 'type' | 'size' | 'upload'
 *   });
 *   up.abort();   // cancel an in-flight upload
 *   up.destroy(); // detach the change listener
 */
(function () {
  'use strict';

  var DEFAULT_TYPES = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp', 'image/heic', 'image/heif'];
  var NAME_RE = /\.(jpe?g|png|webp|heic|heif)$/i;

  function create(opts) {
    if (!opts || !opts.input) return null;

    var input = opts.input;
    var maxBytes = (opts.maxMb || 10) * 1024 * 1024;
    var allowed = opts.allowedTypes || DEFAULT_TYPES;
    var xhr = null;
    var lastDeleteToken = null;

    // Cloudinary returns a delete_token for unsigned uploads when the preset has
    // "Return delete token" enabled. We use it to remove the previously uploaded
    // photo when the shopper swaps in a new one, so abandoned swaps don't pile
    // up in the account. The token is valid for ~10 minutes.
    function deleteByToken(token) {
      if (!token || !opts.cloudName) return;
      try {
        var fd = new FormData();
        fd.append('token', token);
        var dx = new XMLHttpRequest();
        dx.open('POST', 'https://api.cloudinary.com/v1_1/' + encodeURIComponent(opts.cloudName) + '/delete_by_token');
        dx.send(fd);
      } catch (e) {}
    }

    function fail(code) {
      if (typeof opts.onError === 'function') opts.onError(code);
    }

    function revoke(url) {
      if (url) {
        try { URL.revokeObjectURL(url); } catch (e) {}
      }
    }

    function handleFile(file) {
      if (!file) return;

      if (!opts.cloudName || !opts.uploadPreset) {
        fail('config');
        return;
      }

      // Type check — trust the MIME type, fall back to extension (iOS sometimes
      // reports an empty type for HEIC from the camera roll).
      var typeOk = allowed.indexOf(file.type) !== -1 || (!file.type && NAME_RE.test(file.name)) || NAME_RE.test(file.name);
      if (!typeOk) {
        input.value = '';
        fail('type');
        return;
      }

      if (file.size > maxBytes) {
        input.value = '';
        fail('size');
        return;
      }

      var previewUrl = null;
      try { previewUrl = URL.createObjectURL(file); } catch (e) {}
      if (typeof opts.onStart === 'function') opts.onStart(previewUrl, file);

      var data = new FormData();
      data.append('file', file);
      data.append('upload_preset', opts.uploadPreset);
      if (opts.folder) data.append('folder', opts.folder);

      // Optional extra fields resolved at upload time (e.g. tags/context that
      // stamp the order number onto the asset so it's findable in Cloudinary).
      if (typeof opts.getExtraFields === 'function') {
        var extra = opts.getExtraFields() || {};
        Object.keys(extra).forEach(function (k) {
          if (extra[k] != null && extra[k] !== '') data.append(k, extra[k]);
        });
      }

      xhr = new XMLHttpRequest();
      xhr.open('POST', 'https://api.cloudinary.com/v1_1/' + encodeURIComponent(opts.cloudName) + '/image/upload');

      if (xhr.upload) {
        xhr.upload.onprogress = function (event) {
          if (event.lengthComputable && typeof opts.onProgress === 'function') {
            opts.onProgress(Math.round((event.loaded / event.total) * 100));
          }
        };
      }

      xhr.onload = function () {
        revoke(previewUrl);
        var ok = xhr.status >= 200 && xhr.status < 300;
        var res = null;
        if (ok) {
          try { res = JSON.parse(xhr.responseText); } catch (e) { res = null; }
        }
        if (ok && res && res.public_id) {
          var previousToken = lastDeleteToken;
          lastDeleteToken = res.delete_token || null;
          if (typeof opts.onSuccess === 'function') opts.onSuccess(res.public_id, res);
          // Now that the replacement is confirmed, drop the photo it replaced.
          deleteByToken(previousToken);
        } else {
          fail('upload');
        }
        xhr = null;
      };

      xhr.onerror = function () {
        revoke(previewUrl);
        fail('upload');
        xhr = null;
      };

      xhr.send(data);
    }

    function onChange() {
      var file = input.files && input.files[0];
      if (file) handleFile(file);
    }

    input.addEventListener('change', onChange);

    return {
      abort: function () {
        if (xhr) {
          try { xhr.abort(); } catch (e) {}
          xhr = null;
        }
      },
      destroy: function () {
        input.removeEventListener('change', onChange);
      }
    };
  }

  window.VerideCloudinaryUploader = { create: create };
})();
